import {
  useReducer,
  useContext,
  createContext,
  useEffect,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";

const baseUrl = process.env.REACT_APP_BASE_URL;

const LocationsContext = createContext();

const initialState = {
  locations: [],
  locationIsLoading: false,
  currentLocation: {},
  error: "",
};

function reducer(state, action) {
  switch (action.type) {
    case "loading":
      return { ...state, locationIsLoading: true, error: "" };
    case "locations/loaded":
      return { ...state, locationIsLoading: false, locations: action.payload };
    case "location/loaded":
      return {
        ...state,
        locationIsLoading: false,
        currentLocation: action.payload,
      };
    case "location/created":
      return {
        ...state,
        locationIsLoading: false,
        locations: [...state.locations, action.payload],
        currentLocation: action.payload,
      };
    case "location/updated": {
      const found = state.locations.some(
        (location) => location.LocID === action.payload.LocID,
      );
      return {
        ...state,
        locationIsLoading: false,
        locations: found
          ? state.locations.map((location) =>
              location.LocID === action.payload.LocID
                ? action.payload
                : location,
            )
          : [...state.locations, action.payload],
        currentLocation: action.payload,
      };
    }
    case "locationdate/updated":
      return {
        ...state,
        locationIsLoading: false,
        locations: state.locations.map((location) => {
          const updatedLocation = action.payload.find(
            (loc) => loc.LocID === location.LocID,
          );
          return updatedLocation
            ? { ...location, ...updatedLocation }
            : location;
        }),
        currentLocation: action.payload[0] || state.currentLocation,
      };
    case "location/deleted":
      return {
        ...state,
        locationIsLoading: false,
        locations: state.locations.filter(
          (location) => location.LocID !== action.payload,
        ),
        currentLocation: {},
      };
    case "reset":
      return {
        locations: [],
        locationIsLoading: false,
        currentLocation: {},
        error: "",
      };
    case "rejected":
      return { ...state, locationIsLoading: false, error: action.payload };
    default:
      throw new Error("Unknown action type");
  }
}

function LocationsProvider({ children }) {
  const { user, isLoading, fetchWithToken } = useAuth();
  const [{ locations, locationIsLoading, currentLocation, error }, dispatch] =
    useReducer(reducer, initialState);

  // Debug user and token
  useEffect(() => {
    if (isLoading || !user?.CustUUID) {
      console.log("LocationsContext: Waiting for user authentication");
      return;
    }
    console.log("LocationsContext: user:", user);
    console.log("LocationsContext: token:", localStorage.getItem("token"));
  }, [user]);

  // Fetch all locations
  const fetchLocations = useCallback(async () => {
    if (!user?.userId) {
      dispatch({ type: "rejected", payload: "No userId available" });
      return;
    }
    dispatch({ type: "loading" });
    try {
      const res = await fetchWithToken(`${baseUrl}/locations/${user.userId}`);
      const data = await res.json();
      console.log("LocationsContext: fetchLocations response:", data);
      dispatch({ type: "locations/loaded", payload: data.locations || [] });
    } catch (err) {
      dispatch({
        type: "rejected",
        payload: `Error loading locations: ${err.message}`,
      });
    }
  }, [fetchWithToken, user?.userId]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const getLocation = useCallback(
    async (LocID) => {
      if (Number(LocID) === currentLocation.LocID) return;
      if (!user?.userId) {
        dispatch({ type: "rejected", payload: "No userId available" });
        return;
      }
      dispatch({ type: "loading" });
      try {
        const res = await fetchWithToken(
          `${baseUrl}/locations/${LocID}/${user.userId}`,
        );
        const data = await res.json();
        console.log("LocationsContext: getLocation response:", data);
        dispatch({ type: "location/loaded", payload: data });
      } catch (err) {
        dispatch({
          type: "rejected",
          payload: `Error loading location: ${err.message}`,
        });
      }
    },
    [fetchWithToken, user?.userId, currentLocation.LocID],
  );

  const createLocation = useCallback(
    async (newLocation) => {
      if (!user?.userId) {
        dispatch({ type: "rejected", payload: "No userId available" });
        return;
      }
      dispatch({ type: "loading" });
      try {
        const res = await fetchWithToken(
          `${baseUrl}/locations/${user.userId}`,
          {
            method: "POST",
            body: JSON.stringify(newLocation),
            headers: { "Content-Type": "application/json" },
          },
        );
        const data = await res.json();
        console.log("LocationsContext: createLocation response:", data);
        dispatch({ type: "location/created", payload: data.location });
      } catch (err) {
        dispatch({
          type: "rejected",
          payload: `Error creating location: ${err.message}`,
        });
      }
    },
    [fetchWithToken, user?.userId],
  );

  const updateLocation = useCallback(
    async (theLocation) => {
      if (!user?.userId) {
        dispatch({ type: "rejected", payload: "No userId available" });
        return;
      }
      dispatch({ type: "loading" });
      try {
        const res = await fetchWithToken(
          `${baseUrl}/locations/${theLocation.locID}/${user.userId}`,
          {
            method: "POST",
            body: JSON.stringify(theLocation),
            headers: { "Content-Type": "application/json" },
          },
        );
        const data = await res.json();
        console.log("LocationsContext: updateLocation response:", data);
        if (data && data.location && data.location.LocID) {
          dispatch({ type: "location/updated", payload: data.location });
        } else {
          dispatch({
            type: "rejected",
            payload: `Error updating location: Invalid response from server.`,
          });
        }
      } catch (err) {
        dispatch({
          type: "rejected",
          payload: `Error updating location: ${err.message}`,
        });
      }
    },
    [fetchWithToken, user?.userId],
  );

  const updateLocationDates = useCallback(
    async (theLocations) => {
      if (!user?.userId) {
        dispatch({ type: "rejected", payload: "No userId available" });
        return;
      }
      dispatch({ type: "loading" });
      try {
        const res = await fetchWithToken(
          `${baseUrl}/stories/locations/update/${user.userId}`,
          {
            method: "POST",
            body: JSON.stringify(theLocations),
            headers: { "Content-Type": "application/json" },
          },
        );
        const data = await res.json();
        console.log("LocationsContext: updateLocationDates response:", data);
        dispatch({
          type: "locationdate/updated",
          payload: data.locations || [],
        });
      } catch (err) {
        dispatch({
          type: "rejected",
          payload: `Error updating location dates: ${err.message}`,
        });
        throw err; // Propagate to StoryEdit.js
      }
    },
    [fetchWithToken, user?.userId],
  );

  const deleteLocation = useCallback(
    async (LocID) => {
      if (!user?.userId) {
        dispatch({ type: "rejected", payload: "No userId available" });
        return;
      }
      dispatch({ type: "loading" });
      try {
        const res = await fetchWithToken(
          `${baseUrl}/locations/${LocID}/${user.userId}`,
          {
            method: "DELETE",
          },
        );
        console.log("LocationsContext: deleteLocation response:", res.status);
        dispatch({ type: "location/deleted", payload: LocID });
      } catch (err) {
        dispatch({
          type: "rejected",
          payload: `Error deleting location: ${err.message}`,
        });
      }
    },
    [fetchWithToken, user?.userId],
  );

  const resetLocations = useCallback(() => {
    dispatch({ type: "reset" });
  }, []);

  return (
    <LocationsContext.Provider
      value={{
        locations,
        locationIsLoading,
        currentLocation,
        error,
        getLocation,
        createLocation,
        updateLocation,
        updateLocationDates,
        deleteLocation,
        resetLocations,
      }}
    >
      {children}
    </LocationsContext.Provider>
  );
}

function useLocations() {
  const context = useContext(LocationsContext);
  if (context === undefined) {
    throw new Error("LocationsContext used outside of LocationsProvider");
  }
  return context;
}

export { LocationsProvider, useLocations };
